import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  createFilterOptions,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  ListItemText,
  ListSubheader,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { messages } from "../common/notification";
import { fetchRoles } from "../redux/action/roles";
import { createUser, isAdminRole, updateUser } from "../redux/action/users";
import {
  ACCENT,
  LINE,
  MUTED,
  cancelSx,
  dialogStyles as styles,
  inputSx,
  submitSx,
} from "../common/dialogStyles";
import { storeRegion } from "../StoreRegions/functions";
import { useStoreListRefresh } from "../common/useStoreList";

// The store options show the code under the name and sit under a region
// header, so typing any of the three has to find the row - the default filter
// would only ever see getOptionLabel.
const storeFilter = createFilterOptions({
  stringify: (option) =>
    `${option.houseName || ""} ${option.code || ""} ${storeRegion(option)}`,
});

// Pinned to the top of the store list. It acts on whatever the search
// currently matches, so "select all" while filtering picks only those rows.
const SELECT_ALL = { code: "__select_all__", houseName: "Select all" };

// Stores with no region still need a header to sit under, and it goes last.
const NO_REGION = "No region";
const regionOf = (store) => storeRegion(store) || NO_REGION;

// Drives both the rule and the hint that describes it, so the two cannot drift.
const MIN_PASSWORD = 8;
const LENGTH_HINT = `At least ${MIN_PASSWORD} characters`;

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  emailAddress: "",
  password: "",
  phoneNumber: "",
  roleName: "",
  stores: [],
};

// Matches what the server enforces so the obvious mistakes never cost a round
// trip. Phone is the exception - the server does not validate it at all - so
// the 10 digit shape every existing record uses is enforced here.
const validate = (form, isEdit) => {
  const errors = {};

  if (!form.firstName.trim()) errors.firstName = "Required";
  if (!form.lastName.trim()) errors.lastName = "Required";

  // The PATCH endpoint refuses emailAddress and password outright, so neither
  // is shown or checked when editing.
  if (!isEdit) {
    if (!form.emailAddress.trim()) errors.emailAddress = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailAddress.trim()))
      errors.emailAddress = "Enter a valid email address";

    if (form.password.length < MIN_PASSWORD) errors.password = LENGTH_HINT;
  }

  if (!/^\d{10}$/.test(form.phoneNumber.trim()))
    errors.phoneNumber = "Must be exactly 10 digits";
  if (!form.roleName) errors.roleName = "Pick a role";

  return errors;
};

function UserFormDialog({ open, onClose, onSaved, user, storeById }) {
  const isEdit = Boolean(user);
  const [form, setForm] = useState(EMPTY_FORM);
  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // The store list already in redux. Names are shown, codes are submitted.
  const housesList = useSelector((state) => state.housesList);
  // Re-read on open so regions assigned since sign-in show up.
  const { refreshing: refreshingStores, refresh: refreshStores } =
    useStoreListRefresh();

  // Admins are all-stores by role, so the picker is locked rather than filled.
  const allStores = isAdminRole(form.roleName);
  // The stores the open list is showing right now, so Select all knows what
  // it applies to.
  const visibleStores = useRef([]);

  // Grouped by region only once some store actually has one; until then a
  // single "No region" header over every row would just be noise. Autocomplete
  // groups consecutive options, so the list is sorted region first.
  const hasRegions = useMemo(
    () => housesList.some((store) => storeRegion(store)),
    [housesList],
  );
  const storeOptions = useMemo(() => {
    if (!hasRegions) return housesList;
    return [...housesList].sort((a, b) => {
      const ra = regionOf(a);
      const rb = regionOf(b);
      if (ra !== rb) {
        if (ra === NO_REGION) return 1;
        if (rb === NO_REGION) return -1;
        return ra.localeCompare(rb);
      }
      return (a.houseName || "").localeCompare(b.houseName || "");
    });
  }, [housesList, hasRegions]);

  // Adds every code, or removes them all when every one is already picked.
  // Shared by Select all and the region headers.
  const toggleCodes = (codes) => {
    setForm((current) => {
      const allPicked = codes.every((code) => current.stores.includes(code));
      return {
        ...current,
        stores: allPicked
          ? current.stores.filter((code) => !codes.includes(code))
          : [...new Set([...current.stores, ...codes])],
      };
    });
  };

  useEffect(() => {
    if (!open) return;

    // GET /v1/users hands back store ids while the form and the API both work
    // in codes, so the existing assignment is mapped across.
    setForm(
      isEdit
        ? {
            ...EMPTY_FORM,
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            phoneNumber: user.phoneNumber || "",
            roleName: user.roleName || "",
            stores: (user.stores || [])
              .map((id) => storeById?.[id]?.code)
              .filter(Boolean),
          }
        : EMPTY_FORM,
    );
    setErrors({});
    setServerError("");
    setShowPassword(false);

    const loadRoles = async () => {
      setLoadingRoles(true);
      const result = await fetchRoles();
      setLoadingRoles(false);

      if (result.success) setRoles(result.data);
      else setServerError(result.message);
    };

    loadRoles();
  }, [open, isEdit, user, storeById]);

  // Clearing the field's error as it is corrected keeps the message from
  // lingering under a field that is now fine.
  const setField = (field) => (event) => {
    let { value } = event.target;

    // Phone is digits only, capped at the 10 the field asks for, so letters and
    // punctuation cannot be typed at all rather than being rejected on submit.
    if (field === "phoneNumber") value = value.replace(/\D/g, "").slice(0, 10);

    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));
  };

  const handleSubmit = async () => {
    const found = validate(form, isEdit);
    setErrors(found);
    setServerError("");

    if (Object.keys(found).length > 0) return;

    const payload = {
      ...form,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      emailAddress: form.emailAddress.trim(),
      phoneNumber: form.phoneNumber.trim(),
      // Not just the cleared field: an existing admin can be opened for edit
      // with a stores list already on the record.
      stores: allStores ? [] : form.stores,
    };

    setSaving(true);
    const result = isEdit
      ? await updateUser(user.userId, payload)
      : await createUser(payload);
    setSaving(false);

    if (!result.success) {
      setServerError(result.message);
      return;
    }

    messages.success(
      isEdit
        ? `${user.emailAddress} updated`
        : `${payload.emailAddress} registered`,
    );
    onSaved();
    onClose();
  };

  const field = (label, key, extra = {}) => (
    <TextField
      label={label}
      value={form[key]}
      onChange={setField(key)}
      error={Boolean(errors[key])}
      helperText={errors[key] || extra.helperText || " "}
      fullWidth
      size="small"
      sx={inputSx}
      {...extra.props}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "16px" } }}
      // Enter submits, so the form does not need the mouse to finish.
      onKeyDown={(event) => {
        if (event.key === "Enter" && !saving) handleSubmit();
      }}
    >
      <div style={styles.header}>
        <div style={styles.badge}>
          <PersonAddAlt1Icon />
        </div>
        <div style={{ flex: 1 }}>
          <div style={styles.title}>
            {isEdit ? "Edit User" : "Register User"}
          </div>
          <div style={styles.subtitle}>
            {isEdit
              ? user.emailAddress
              : "Create an account and choose what it can reach"}
          </div>
        </div>
        <IconButton onClick={onClose} size="small" sx={{ color: MUTED }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent sx={{ padding: "4px 24px 8px" }}>
        {serverError && (
          <Alert
            severity="error"
            sx={{
              marginTop: "16px",
              borderRadius: "12px",
              fontFamily: "manrope",
            }}
          >
            {serverError}
          </Alert>
        )}

        <div style={styles.section}>
          <div style={styles.sectionLabel}>Personal details</div>
          <div style={styles.row}>
            {field("First name", "firstName")}
            {field("Last name", "lastName")}
          </div>
          <div style={styles.row}>
            {!isEdit &&
              field("Email address", "emailAddress", {
                props: { type: "email" },
              })}
            {field("Phone number", "phoneNumber", {
              helperText: form.phoneNumber.length < 10 ? "10 digits" : "",
              // No maxLength: it caps raw characters before the digits-only
              // filter runs, so pasting "abc1234567890" would keep just 7
              // digits. The onChange slice is what enforces the 10.
              props: {
                inputMode: "numeric",
                autoComplete: "tel",
              },
            })}
          </div>
        </div>

        {!isEdit && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>Password</div>
            {field("Password", "password", {
              // Cleared once satisfied; the field's " " fallback keeps the row.
              helperText:
                form.password.length < MIN_PASSWORD ? LENGTH_HINT : "",
              props: {
                type: showPassword ? "text" : "password",
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? "Hide" : "Show"}>
                        <IconButton
                          onClick={() => setShowPassword((shown) => !shown)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              },
            })}
          </div>
        )}

        <div style={styles.section}>
          <div style={styles.sectionLabel}>Access</div>

          <div style={styles.row}>
            <Autocomplete
              fullWidth
              size="small"
              sx={{
                ...styles.rowItem,
                "& .MuiAutocomplete-inputRoot": { textTransform: "capitalize" },
              }}
              options={roles}
              loading={loadingRoles}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) =>
                option.name === value.name
              }
              value={roles.find((role) => role.name === form.roleName) || null}
              onChange={(event, picked) => {
                const roleName = picked ? picked.name : "";
                setForm((current) => ({
                  ...current,
                  roleName,
                  // Admins reach every store, so a selection would be ignored
                  // by the server and misleading in the form.
                  stores: isAdminRole(roleName) ? [] : current.stores,
                }));
                setErrors((current) => ({ ...current, roleName: "" }));
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{ ...props.style, textTransform: "capitalize" }}
                >
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Role"
                  placeholder="Search roles..."
                  error={Boolean(errors.roleName)}
                  helperText={errors.roleName || " "}
                  sx={inputSx}
                />
              )}
            />

            {/* Admins reach every store by role, so there is nothing to
                pick. Hidden rather than shown empty, and the submit
                payload clears stores for an admin either way. */}
            {!allStores && (
              <Autocomplete
                fullWidth
                size="small"
                multiple
                disableCloseOnSelect
                disableClearable
                sx={styles.rowItem}
                options={storeOptions}
                onOpen={refreshStores}
                loading={refreshingStores && housesList.length === 0}
                groupBy={
                  hasRegions
                    ? (option) =>
                        option === SELECT_ALL ? SELECT_ALL.code : regionOf(option)
                    : undefined
                }
                renderGroup={(params) => {
                  if (params.group === SELECT_ALL.code) return params.children;

                  // Acts on the region's stores the search is showing, like
                  // Select all does for the whole list.
                  const codes = visibleStores.current
                    .filter((item) => regionOf(item) === params.group)
                    .map((item) => item.code);
                  const pickedCount = codes.filter((code) =>
                    form.stores.includes(code),
                  ).length;

                  return (
                    <li key={params.key}>
                      <ListSubheader
                        onClick={() => toggleCodes(codes)}
                        sx={{
                          top: "-8px",
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          lineHeight: "36px",
                          padding: "0 12px",
                          fontFamily: "manrope",
                          fontSize: "12px",
                          fontWeight: 700,
                          color: MUTED,
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                          backgroundColor: "#F6F8FD",
                          borderBottom: `1px solid ${LINE}`,
                        }}
                      >
                        <Checkbox
                          checked={codes.length > 0 && pickedCount === codes.length}
                          indeterminate={
                            pickedCount > 0 && pickedCount < codes.length
                          }
                          size="small"
                          sx={{
                            marginRight: "4px",
                            "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                              color: ACCENT,
                            },
                          }}
                        />
                        <span style={{ flex: 1 }}>{params.group}</span>
                        <span style={{ textTransform: "none", fontWeight: 600 }}>
                          {pickedCount}/{codes.length}
                        </span>
                      </ListSubheader>
                      <ul style={{ padding: 0 }}>{params.children}</ul>
                    </li>
                  );
                }}
                filterOptions={(options, state) => {
                  const matches = storeFilter(options, state);
                  visibleStores.current = matches;
                  return matches.length ? [SELECT_ALL, ...matches] : [];
                }}
                getOptionLabel={(option) =>
                  option.houseName || option.code || ""
                }
                isOptionEqualToValue={(option, value) =>
                  option.code === value.code
                }
                // form.stores holds codes, which is what the API wants; the
                // Autocomplete works in store objects, so map between the two.
                value={housesList.filter((item) =>
                  form.stores.includes(item.code),
                )}
                onChange={(event, picked, reason, details) => {
                  if (details?.option === SELECT_ALL) {
                    toggleCodes(visibleStores.current.map((item) => item.code));
                    return;
                  }
                  setForm((current) => ({
                    ...current,
                    stores: picked.map((item) => item.code),
                  }));
                }}
                renderOption={(props, option, { selected }) => {
                  if (option === SELECT_ALL) {
                    const visible = visibleStores.current;
                    const pickedCount = visible.filter((item) =>
                      form.stores.includes(item.code),
                    ).length;
                    return (
                      <li
                        {...props}
                        key={SELECT_ALL.code}
                        style={{ borderBottom: `1px solid ${LINE}` }}
                      >
                        <Checkbox
                          checked={
                            visible.length > 0 && pickedCount === visible.length
                          }
                          indeterminate={
                            pickedCount > 0 && pickedCount < visible.length
                          }
                          size="small"
                          sx={{
                            marginRight: "4px",
                            "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                              color: ACCENT,
                            },
                          }}
                        />
                        <ListItemText
                          primary={
                            visible.length === housesList.length
                              ? "Select all"
                              : `Select all ${visible.length} matching`
                          }
                          primaryTypographyProps={{
                            fontFamily: "manrope",
                            fontSize: "14px",
                            fontWeight: 600,
                          }}
                        />
                      </li>
                    );
                  }
                  return (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        size="small"
                        sx={{
                          marginRight: "4px",
                          "&.Mui-checked": { color: ACCENT },
                        }}
                      />
                      <ListItemText
                        primary={option.houseName}
                        secondary={option.code}
                        primaryTypographyProps={{
                          fontFamily: "manrope",
                          fontSize: "14px",
                        }}
                        secondaryTypographyProps={{
                          fontFamily: "manrope",
                          fontSize: "11.5px",
                        }}
                      />
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Stores"
                    placeholder={form.stores.length ? "" : "Search stores..."}
                    helperText={
                      form.stores.length === 0
                        ? "None selected means no store access"
                        : `${form.stores.length} of ${housesList.length} selected`
                    }
                    sx={inputSx}
                  />
                )}
                // Rendered as one line of text rather than chips: chips wrap and
                // make the field grow taller and wider than the Role field next
                // to it. What is selected is still visible in the open list.
                renderTags={(selected) => (
                  <span
                    style={{
                      fontFamily: "manrope",
                      fontSize: "14px",
                      color: ACCENT,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                    }}
                  >
                    {`${selected.length} store${
                      selected.length === 1 ? "" : "s"
                    } selected`}
                  </span>
                )}
              />
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions
        sx={{ padding: "16px 24px 20px", borderTop: `1px solid ${LINE}` }}
      >
        <Button onClick={onClose} disabled={saving} sx={cancelSx}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          disableElevation
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
            ) : null
          }
          sx={submitSx}
        >
          {saving
            ? isEdit
              ? "Saving"
              : "Registering"
            : isEdit
              ? "Save changes"
              : "Register user"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UserFormDialog;
